import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";
import CricketSummaryTab from "./cricket/CricketSummaryTab";
import CricketRealtimeTab from "./cricket/CricketRealtimeTab";

/**
 * JSON shape we persist per cricket match
 * {
 *   matchType: "t20" | "odi" | "limited-overs",
 *   totalOvers: number,
 *   team1Name: string,
 *   team2Name: string,
 *   team1Players: [{ id, userId, name, isCustom }],
 *   team2Players: [{ id, userId, name, isCustom }],
 *   tossWinner: "team1" | "team2",
 *   tossDecision: "bat" | "bowl",
 *   ballEvents: [
 *     {
 *       innings: "team1" | "team2",
 *       strikerId: string,
 *       nonStrikerId: string,
 *       bowlerId: string,
 *       runsOffBat: number,
 *       extraType: "none" | "wide" | "no-ball" | "bye" | "leg-bye" | "penalty",
 *       extraRuns: number,
 *       isLegalDelivery: boolean,
 *       wicket: { isWicket: boolean, kind: string, playerOutId: string, fielder: string }
 *     }
 *   ],
 *   innings: {
 *     team1: { totalRuns, wickets, overs, balls, extras, battingStats, bowlingStats, fallOfWickets },
 *     team2: { ...same }
 *   },
 *   winner: "team1" | "team2" | "draw" | null,
 *   result: string
 * }
 */

export const AddCricket = () => {
  const location = useLocation();
  const data = location.state || {};
  const navigate = useNavigate();

  const [cricketMatches, setCricketMatches] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("summary"); // summary | realtime
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          const currentUserId = user?.uid;
          const invitedUsers = (data.invitedUsers || []).map((u) => ({
            ...u,
            isInvited: true,
            invitationStatus: u.status,
          }));

          const currentUser = users.find((u) => u.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          const otherUsers = users.filter(
            (u) =>
              !invitedUsers.some((invited) => invited.user_id === u.id) &&
              u.id != currentUserId
          );

          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers, user?.uid]);

  const createEmptyInnings = () => ({
    totalRuns: 0,
    wickets: 0,
    overs: 0,
    balls: 0,
    extras: {
      byes: 0,
      legByes: 0,
      wides: 0,
      noBalls: 0,
      penalties: 0,
    },
    battingStats: [],
    bowlingStats: [],
    fallOfWickets: [],
  });

  const createEmptyMatch = () => ({
    matchType: "limited-overs",
    totalOvers: 20,
    team1Name: "",
    team2Name: "",
    team1Players: [],
    team2Players: [],
    tossWinner: null,
    tossDecision: null,
    ballEvents: [],
    innings: {
      team1: createEmptyInnings(),
      team2: createEmptyInnings(),
    },
    winner: null,
    result: "",
  });

  const validateMatches = () => {
    const errors = [];

    cricketMatches.forEach((match, index) => {
      if (!match.team1Name || !match.team2Name) {
        errors.push(`Match ${index + 1}: Team names are required`);
      }

      if (match.team1Players.length !== match.team2Players.length) {
        errors.push(
          `Match ${index + 1}: Both teams must have equal number of players`
        );
      }

      if (match.team1Players.length < 2 || match.team2Players.length < 2) {
        errors.push(
          `Match ${index + 1}: Each team must have at least 2 players`
        );
      }

      if (match.team1Players.length > 11 || match.team2Players.length > 11) {
        errors.push(
          `Match ${index + 1}: Each team cannot have more than 11 players`
        );
      }

      if (!match.totalOvers || match.totalOvers < 1 || match.totalOvers > 50) {
        errors.push(`Match ${index + 1}: Total overs must be between 1 and 50`);
      }

      if (!match.tossWinner) {
        errors.push(`Match ${index + 1}: Toss winner must be selected`);
      }

      if (!match.tossDecision) {
        errors.push(
          `Match ${index + 1}: Toss decision (bat/bowl) must be selected`
        );
      }

      ["team1", "team2"].forEach((team) => {
        const innings = match.innings[team];
        const teamName =
          team === "team1"
            ? match.team1Name || "Team 1"
            : match.team2Name || "Team 2";

        const hasAnyBattingStats = innings.battingStats.some(
          (stat) => stat.runs > 0 || stat.balls > 0
        );
        const hasAnyBowlingStats = innings.bowlingStats.some(
          (stat) => stat.overs > 0 || stat.balls > 0
        );

        if (
          !hasAnyBattingStats &&
          !hasAnyBowlingStats &&
          innings.totalRuns === 0
        ) {
          errors.push(
            `Match ${
              index + 1
            }: ${teamName} innings has no data - please add batting/bowling statistics`
          );
        }

        innings.battingStats.forEach((stat, statIndex) => {
          if (stat.runs < 0) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Batsman ${
                statIndex + 1
              } runs cannot be negative`
            );
          }
          if (stat.balls < 0) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Batsman ${
                statIndex + 1
              } balls cannot be negative`
            );
          }
          if (stat.balls > match.totalOvers * 6) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Batsman ${
                statIndex + 1
              } balls (${stat.balls}) cannot exceed total possible balls (${
                match.totalOvers * 6
              })`
            );
          }
          if (stat.fours < 0 || stat.sixes < 0) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Batsman ${
                statIndex + 1
              } fours/sixes cannot be negative`
            );
          }
          const boundaryRuns = stat.fours * 4 + stat.sixes * 6;
          if (boundaryRuns > stat.runs) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Batsman ${
                statIndex + 1
              } boundary runs (${boundaryRuns}) cannot exceed total runs (${
                stat.runs
              })`
            );
          }
        });

        innings.bowlingStats.forEach((stat, statIndex) => {
          if (stat.overs < 0) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Bowler ${
                statIndex + 1
              } overs cannot be negative`
            );
          }
          if (stat.overs > match.totalOvers) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Bowler ${
                statIndex + 1
              } overs (${stat.overs}) cannot exceed match total overs (${
                match.totalOvers
              })`
            );
          }
          if (stat.balls < 0 || stat.balls > 5) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Bowler ${
                statIndex + 1
              } balls must be between 0-5`
            );
          }
          if (stat.runs < 0) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Bowler ${
                statIndex + 1
              } runs cannot be negative`
            );
          }
          if (stat.wickets < 0 || stat.wickets > 10) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Bowler ${
                statIndex + 1
              } wickets must be between 0-10`
            );
          }
          if (stat.maidens < 0 || stat.maidens > stat.overs) {
            errors.push(
              `Match ${index + 1}, ${teamName}: Bowler ${
                statIndex + 1
              } maidens cannot exceed overs bowled`
            );
          }
        });

        const totalBowlingOvers = innings.bowlingStats.reduce((total, stat) => {
          return total + stat.overs + stat.balls / 6;
        }, 0);

        if (totalBowlingOvers > match.totalOvers) {
          errors.push(
            `Match ${
              index + 1
            }, ${teamName}: Total bowling overs (${totalBowlingOvers.toFixed(
              1
            )}) cannot exceed match overs (${match.totalOvers})`
          );
        }

        if (innings.wickets > match[`${team}Players`].length - 1) {
          errors.push(
            `Match ${index + 1}, ${teamName}: Wickets (${
              innings.wickets
            }) cannot exceed available batsmen (${
              match[`${team}Players`].length - 1
            })`
          );
        }

        Object.entries(innings.extras).forEach(([extraType, value]) => {
          if (value < 0) {
            errors.push(
              `Match ${index + 1}, ${teamName}: ${extraType} cannot be negative`
            );
          }
        });
      });

      if (!match.winner) {
        errors.push(`Match ${index + 1}: Match winner must be declared`);
      }

      if (!match.result || match.result.trim() === "") {
        errors.push(`Match ${index + 1}: Match result description is required`);
      }
    });

    return errors;
  };

  const handleSaveAll = async () => {
    const validationErrors = validateMatches();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: cricketMatches,
      };

      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      await response.json();
      toast.success("Saved all cricket matches!");

      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  const handleAddCricketMatch = () => {
    setCricketMatches((prev) => [...prev, createEmptyMatch()]);
  };

  const handleRemoveMatch = (index) => {
    setCricketMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setCricketMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  const aggregateInningsFromEvents = (
    events,
    battingPlayers,
    bowlingPlayers
  ) => {
    const sameId = (a, b) => String(a) === String(b);
    const innings = createEmptyInnings();
    const battingMap = {};
    const bowlingMap = {};
    let legalBalls = 0;

    const findName = (id) =>
      battingPlayers.find((p) => sameId(p.userId, id))?.name ||
      bowlingPlayers.find((p) => sameId(p.userId, id))?.name ||
      "Player";

    events.forEach((ball) => {
      const runsOffBat = Number(ball.runsOffBat || 0);
      const extraType = ball.extraType || "none";
      const extraRuns = Number(ball.extraRuns || 0);
      const isLegal =
        ball.isLegalDelivery ?? (extraType !== "wide" && extraType !== "no-ball");

      const strikerId = ball.strikerId;
      const bowlerId = ball.bowlerId;

      if (!battingMap[String(strikerId)]) {
        battingMap[String(strikerId)] = {
          playerId: String(strikerId),
          playerName: findName(strikerId),
          runs: 0,
          balls: 0,
          fours: 0,
          sixes: 0,
          isOut: false,
          howOut: "",
          bowlerOut: "",
        };
      }
      if (!bowlingMap[String(bowlerId)]) {
        bowlingMap[String(bowlerId)] = {
          playerId: String(bowlerId),
          playerName: findName(bowlerId),
          overs: 0,
          balls: 0,
          maidens: 0,
          runs: 0,
          wickets: 0,
          economy: 0,
        };
      }

      const batStat = battingMap[String(strikerId)];
      const bowlStat = bowlingMap[String(bowlerId)];

      if (isLegal) {
        batStat.balls += 1;
        legalBalls += 1;
      }

      batStat.runs += runsOffBat;
      if (runsOffBat === 4) batStat.fours += 1;
      if (runsOffBat === 6) batStat.sixes += 1;

      let extrasAdded = 0;
      if (extraType === "wide") {
        innings.extras.wides += extraRuns || 1;
        extrasAdded = extraRuns || 1;
      } else if (extraType === "no-ball") {
        innings.extras.noBalls += extraRuns || 1;
        extrasAdded = extraRuns || 1;
      } else if (extraType === "bye") {
        innings.extras.byes += extraRuns;
        extrasAdded = extraRuns;
      } else if (extraType === "leg-bye") {
        innings.extras.legByes += extraRuns;
        extrasAdded = extraRuns;
      } else if (extraType === "penalty") {
        innings.extras.penalties += extraRuns;
        extrasAdded = extraRuns;
      }

      const totalThisBall = runsOffBat + extrasAdded;
      innings.totalRuns += totalThisBall;

      if (ball.wicket?.isWicket) {
        const outId =
          ball.wicket.playerOutId || ball.strikerId || battingPlayers[0]?.userId;
        if (!battingMap[String(outId)]) {
          battingMap[String(outId)] = {
            playerId: String(outId),
            playerName: findName(outId),
            runs: 0,
            balls: 0,
            fours: 0,
            sixes: 0,
            isOut: false,
            howOut: "",
            bowlerOut: "",
          };
        }
        battingMap[String(outId)].isOut = true;
        battingMap[String(outId)].howOut = ball.wicket.kind || "wicket";
        battingMap[String(outId)].bowlerOut = findName(bowlerId);
        innings.wickets += 1;
        bowlStat.wickets += 1;
      }

      bowlStat.runs += totalThisBall;
      if (isLegal) {
        bowlStat.balls += 1;
        if (bowlStat.balls === 6) {
          bowlStat.overs += 1;
          bowlStat.balls = 0;
        }
      }
    });

    innings.overs = Math.floor(legalBalls / 6);
    innings.balls = legalBalls % 6;

    innings.battingStats = Object.values(battingMap);
    innings.bowlingStats = Object.values(bowlingMap).map((b) => {
      const oversDecimal = b.overs + b.balls / 6;
      return {
        ...b,
        economy: oversDecimal > 0 ? (b.runs / oversDecimal).toFixed(2) : "0.00",
      };
    });

    return innings;
  };

  const buildMatchFromEvents = (match, events) => {
    const team1Innings = aggregateInningsFromEvents(
      events.filter((e) => e.innings === "team1"),
      match.team1Players,
      match.team2Players
    );

    const team2Innings = aggregateInningsFromEvents(
      events.filter((e) => e.innings === "team2"),
      match.team2Players,
      match.team1Players
    );

    return {
      ...match,
      ballEvents: events,
      innings: {
        team1: team1Innings,
        team2: team2Innings,
      },
    };
  };

  const handleEventsChange = (matchIndex, events) => {
    setCricketMatches((prev) =>
      prev.map((m, idx) => (idx === matchIndex ? buildMatchFromEvents(m, events) : m))
    );
  };

  const brandBlue = "#3498db";
  const tabBaseStyle = {
    padding: "12px 16px",
    fontWeight: 600,
    fontSize: "16px",
    color: "#718096",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };
  const tabActiveStyle = {
    color: brandBlue,
    borderBottom: `2px solid ${brandBlue}`,
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />
      <div className="max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="border-b-2 border-[#3498db] pb-4 mb-6">
          <h2 className="text-3xl font-semibold text-gray-800 mb-2">
            {data.sport} Matches at {data.arenaBooking?.name}
          </h2>
          <p className="text-lg text-gray-600">
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed cricket match information"
            )}
          </p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("summary")}
            style={{
              ...tabBaseStyle,
              ...(activeTab === "summary" ? tabActiveStyle : {}),
            }}
          >
            Summary / Manual
          </button>
          <button
            onClick={() => setActiveTab("realtime")}
            style={{
              ...tabBaseStyle,
              ...(activeTab === "realtime" ? tabActiveStyle : {}),
            }}
          >
            Real-time (Ball-by-ball)
          </button>
        </div>

        {activeTab === "summary" ? (
          <CricketSummaryTab
            cricketMatches={cricketMatches}
            availableUsers={availableUsers}
            onAddMatch={handleAddCricketMatch}
            onUpdateMatch={handleUpdateMatch}
            onRemoveMatch={handleRemoveMatch}
            onSaveAll={handleSaveAll}
          />
        ) : (
          <CricketRealtimeTab
            cricketMatches={cricketMatches}
            onAddMatch={handleAddCricketMatch}
            onEventsChange={handleEventsChange}
            buildMatchFromEvents={buildMatchFromEvents}
            setActiveTab={setActiveTab}
          />
        )}
      </div>
    </>
  );
};

export default AddCricket;

